import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/context/AuthContext';
import { stripeConnectApi, settingsApi } from '@/lib/api';
import { 
  Building2, 
  CreditCard, 
  CheckCircle, 
  AlertCircle, 
  Loader2, 
  ExternalLink,
  ArrowLeft,
  RefreshCw,
  Shield,
  DollarSign,
  Clock,
  Info
} from 'lucide-react';

const statusConfig = {
  not_created: {
    color: 'bg-gray-500/15 text-gray-500',
    icon: CreditCard,
    title: 'Not Connected',
    description: 'Connect your bank account to receive automatic payouts'
  },
  pending: {
    color: 'bg-yellow-500/15 text-yellow-600',
    icon: Clock,
    title: 'Setup Incomplete',
    description: 'Complete your account setup to start receiving payouts'
  },
  incomplete: {
    color: 'bg-orange-500/15 text-orange-500',
    icon: AlertCircle,
    title: 'Additional Info Required',
    description: 'Stripe needs more information to verify your account'
  },
  under_review: {
    color: 'bg-blue-500/15 text-blue-500',
    icon: Clock,
    title: 'Under Review',
    description: 'Stripe is reviewing your account information'
  },
  active: {
    color: 'bg-green-500/15 text-green-500',
    icon: CheckCircle,
    title: 'Active',
    description: 'Your account is ready to receive payouts'
  }
};

export default function StripeConnectPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshProfile } = useAuth();
  const [accountStatus, setAccountStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [platformFee, setPlatformFee] = useState(10);

  useEffect(() => {
    // Check if returning from Stripe onboarding
    const returnStatus = searchParams.get('status');
    if (returnStatus === 'success') {
      toast.success('Stripe account setup updated!');
      refreshProfile();
    } else if (returnStatus === 'refresh') {
      toast.info('Please complete your Stripe account setup');
    }
    
    fetchData();
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [statusRes, feeRes] = await Promise.all([
        stripeConnectApi.getAccountStatus(),
        settingsApi.getPlatformFee()
      ]);
      setAccountStatus(statusRes.data);
      setPlatformFee(feeRes.data.fee_percent);
    } catch (error) {
      toast.error('Failed to fetch account status');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountStatus = async () => {
    try {
      const response = await stripeConnectApi.getAccountStatus();
      setAccountStatus(response.data);
    } catch (error) {
      toast.error('Failed to fetch account status');
    }
  };

  const handleConnectAccount = async () => {
    setConnecting(true);
    try {
      const response = await stripeConnectApi.createAccount(window.location.origin);
      // Open Stripe onboarding in new tab
      window.open(response.data.onboarding_url, '_blank');
      toast.success('Opening Stripe Connect setup...');
      
      // Wait a moment then refresh status
      setTimeout(() => {
        fetchAccountStatus();
        setConnecting(false);
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Connect account');
      setConnecting(false);
    }
  };

  const handleContinueOnboarding = async () => {
    setConnecting(true);
    try {
      const response = await stripeConnectApi.createOnboardingLink(window.location.origin);
      window.open(response.data.onboarding_url, '_blank');
      toast.success('Opening Stripe setup...');
      
      setTimeout(() => {
        fetchAccountStatus();
        setConnecting(false);
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create onboarding link');
      setConnecting(false);
    }
  };

  const handleViewDashboard = async () => {
    try {
      const response = await stripeConnectApi.getDashboardLink();
      window.open(response.data.dashboard_url, '_blank');
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const status = accountStatus?.status || 'not_created';
  const config = statusConfig[status] || statusConfig.not_created;
  const StatusIcon = config.icon;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/provider" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
            <Button variant="ghost" size="sm" onClick={fetchAccountStatus} data-testid="refresh-status-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              Payout Settings
            </h1>
            <p className="text-muted-foreground">
              Connect your bank account to receive automatic payouts when jobs are completed
            </p>
          </div>

          {/* Status Card */}
          <Card className="mb-6">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Stripe Connect Account
                </CardTitle>
                <Badge className={config.color} data-testid="connect-status-badge">
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {config.title}
                </Badge>
              </div>
              <CardDescription>{config.description}</CardDescription>
            </CardHeader>
            <CardContent>
              {accountStatus?.has_account ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Charges Enabled:</span>
                      {accountStatus.charges_enabled ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-yellow-500" />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Payouts Enabled:</span>
                      {accountStatus.payouts_enabled ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-yellow-500" />
                      )}
                    </div>
                  </div>

                  {accountStatus.requirements?.currently_due?.length > 0 && (
                    <Alert variant="warning">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        {accountStatus.requirements.currently_due.length} item(s) need your attention to complete setup
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="flex gap-3">
                    {status !== 'active' && (
                      <Button 
                        onClick={handleContinueOnboarding} 
                        disabled={connecting}
                        className="flex-1 btn-active"
                        data-testid="continue-setup-btn"
                      >
                        {connecting ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <ExternalLink className="w-4 h-4 mr-2" />
                        )}
                        Complete Setup
                      </Button>
                    )}
                    {status === 'active' && (
                      <Button 
                        onClick={handleViewDashboard}
                        className="flex-1"
                        variant="outline"
                        data-testid="view-dashboard-btn"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Stripe Dashboard
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <Button 
                  onClick={handleConnectAccount} 
                  disabled={connecting}
                  className="w-full btn-active"
                  size="lg"
                  data-testid="connect-account-btn"
                >
                  {connecting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  Connect Bank Account
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Info Cards */}
          <div className="grid gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <DollarSign className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">How You Get Paid</h3>
                    <p className="text-sm text-muted-foreground">
                      {platformFee === 0 
                        ? "🎉 Promotion active! You'll receive 100% of the quoted amount directly to your bank account once the job is completed."
                        : `Once a job is completed and confirmed by both parties, you'll receive ${100 - platformFee}% of your quoted price directly to your connected bank account. Payouts are processed automatically - no invoicing required.`
                      }
                    </p>
                    {platformFee > 0 && (
                      <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm">
                        <p className="text-sm text-amber-700 font-semibold">
                          ⚠️ Important: Always factor the {platformFee}% platform fee into your quotes to protect your earnings. This fee is deducted from every completed job.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Secure Payment Protection</h3>
                    <p className="text-sm text-muted-foreground">
                      When a customer books your service, their payment is held securely by Stripe until the job is completed. This protects both you and the customer. Your banking details are never stored on our servers.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={platformFee === 0 ? 'border-green-500/30 bg-green-500/5' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg ${platformFee === 0 ? 'bg-green-500/10' : 'bg-primary/10'}`}>
                    <Info className={`w-5 h-5 ${platformFee === 0 ? 'text-green-500' : 'text-primary'}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">
                      {platformFee === 0 ? '🎉 Promotional Fee Structure' : 'Fee Structure'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      <strong>Your payout:</strong> {100 - platformFee}% of your quoted price<br />
                      <strong>Platform fee:</strong> {platformFee}% {platformFee === 0 ? '(Promo!)' : '(helps us run the marketplace)'}<br />
                      <strong>Customer pays:</strong> Your quoted price only - no extra charges for them
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
